import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useSearchStore } from "../store/searchStore";
import { api, ISearchProduct, ISearchResponse, IStockContactResponse } from "../api/api";
import MainLayout from "../components/MainLayout";
import usePageTitle from "../utils/usePageTitle";
import SkeletonLoader from "../components/SkeletonLoader";
import CustomDropdown from "../components/CustomDropdown";
import MultiSelectDropdown from "../components/MultiSelectDropdown";
import { useToast } from "../components/ToastContainer";
import { useDebounce } from "use-debounce";

const ITEMS_PER_PAGE = 16;

const PageContainer = styled.div`
  width: 100%;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 80px);
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
  flex-wrap: wrap;
  gap: 16px;
`;

const PageTitle = styled.h1`
  font-family: "Ubuntu", sans-serif;
  font-weight: 500;
  font-size: 24px;
  color: #ffffff;
  margin: 0;

  @media (min-width: 768px) {
    font-size: 28px;
  }

  @media (min-width: 1024px) {
    font-size: 32px;
  }
`;

const SearchCounter = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-family: "Ubuntu", sans-serif;
  font-size: 14px;
  color: #838D90;
`;

const CounterNumber = styled.span`
  font-weight: 600;
  color: #24BA7D;
`;

const CounterText = styled.span`
  font-weight: 400;
`;

const FiltersContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 24px;

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: flex-end;
  }
`;

const SearchContainer = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  padding: 10px 16px;
  border: 2px solid #609f94;
  border-radius: 9999px;
`;

const FilterContainer = styled.div`
  width: 100%;

  @media (min-width: 768px) {
    width: 280px;
    flex-shrink: 0;
  }
`;

const SearchInput = styled.input`
  background: transparent;
  border: none;
  outline: none;
  width: 100%;
  font-family: "Ubuntu", sans-serif;
  font-size: 14px;
  color: #ffffff;

  @media (min-width: 1024px) {
    font-size: 16px;
  }

  &::placeholder {
    color: #609f94;
  }
`;

const ProductsContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 24px;
  }

  @media (min-width: 1440px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const ProductCard = styled.div`
  background-color: #1a2e2e;
  border-radius: 12px;
  border: 1px solid #2c4a4a;
  padding: 16px;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  }
`;

const ProductImage = styled.div`
  width: 100%;
  height: 200px;
  background-color: transparent;
  border-radius: 8px;
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
  margin-bottom: 12px;
`;

const ProductName = styled.h3`
  font-family: "Ubuntu", sans-serif;
  font-weight: 500;
  font-size: 16px;
  color: #ffffff;
  margin: 0 0 8px 0;
  line-height: 1.3;
`;

const ProductDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 12px;
`;

const ProductColor = styled.span`
  font-family: "Ubuntu", sans-serif;
  font-weight: 400;
  font-size: 14px;
  color: #838d90;
`;

const ArticleInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 12px;
`;

const ArticleLabel = styled.span`
  font-family: "Ubuntu", sans-serif;
  font-weight: 400;
  font-size: 11px;
  color: #609f94;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ArticleValue = styled.span`
  font-family: "Ubuntu", sans-serif;
  font-weight: 500;
  font-size: 13px;
  color: #ffffff;
  background-color: rgba(255, 255, 255, 0.06);
  padding: 4px 8px;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  display: inline-block;
  width: fit-content;
`;

const ProductInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

const SizeInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const SizeLabel = styled.span`
  font-family: "Ubuntu", sans-serif;
  font-weight: 400;
  font-size: 11px;
  color: #609f94;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const SizesContainer = styled.div`
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
`;

const SizeBadge = styled.span`
  background-color: rgba(255, 255, 255, 0.08);
  color: #ffffff;
  padding: 2px 6px;
  border-radius: 4px;
  font-family: "Ubuntu", sans-serif;
  font-weight: 500;
  font-size: 11px;
  border: 1px solid rgba(255, 255, 255, 0.12);
`;

const PriceInfo = styled.div`
  text-align: right;
`;

const Price = styled.span`
  font-family: "Ubuntu", sans-serif;
  font-weight: 600;
  font-size: 18px;
  color: #24ba7d;
  white-space: nowrap;
`;

const ConditionBadge = styled.span`
  background-color: rgba(36, 186, 125, 0.1);
  color: #24ba7d;
  padding: 4px 8px;
  border-radius: 6px;
  font-family: "Ubuntu", sans-serif;
  font-weight: 400;
  font-size: 12px;
  margin-bottom: 8px;
  display: inline-block;
`;

const LocationInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
`;

const LocationText = styled.span`
  font-family: "Ubuntu", sans-serif;
  font-weight: 400;
  font-size: 12px;
  color: #609f94;
`;

const FittingBadge = styled.span`
  background-color: rgba(36, 186, 125, 0.1);
  color: #24ba7d;
  padding: 2px 6px;
  border-radius: 4px;
  font-family: "Ubuntu", sans-serif;
  font-weight: 400;
  font-size: 10px;
  border: 1px solid rgba(36, 186, 125, 0.3);
`;

const VendorInfo = styled.div<{ $showContacts?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-top: 12px;
  border-top: 1px solid #2c4a4a;
  position: relative;
`;

const VendorContent = styled.div<{ $showContacts?: boolean }>`
  filter: ${props => (props.$showContacts ? 'none' : 'blur(3px)')};
  transition: filter 0.3s ease;
  /* блокируем клики по размытым ссылкам/контенту */
  pointer-events: ${props => (props.$showContacts ? 'auto' : 'none')};
`;

const ShowContactsButton = styled.button<{ $showContacts?: boolean }>`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: rgba(36, 186, 125, 0.9);
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 12px;
  font-family: "Ubuntu", sans-serif;
  cursor: pointer;
  z-index: 2;
  transition: all 0.3s ease;
  opacity: ${props => props.$showContacts ? 0 : 1};
  pointer-events: ${props => props.$showContacts ? 'none' : 'auto'};
  
  &:hover {
    background: rgba(36, 186, 125, 1);
    transform: translate(-50%, -50%) scale(1.05);
  }
`;

const VendorLabel = styled.span`
  font-family: "Ubuntu", sans-serif;
  font-weight: 400;
  font-size: 11px;
  color: #609f94;
  text-transform: uppercase;
`;

const VendorName = styled.span`
  font-family: "Ubuntu", sans-serif;
  font-weight: 500;
  font-size: 14px;
  color: #ffffff;
  margin-bottom: 4px;
`;

const VendorNameContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 6px;
  margin-bottom: 4px;
  align-items: baseline;
  flex-wrap: wrap;
`;

const VendorFirstName = styled.span`
  font-family: "Ubuntu", sans-serif;
  font-weight: 500;
  font-size: 14px;
  color: #ffffff;
`;

const VendorLastName = styled.span`
  font-family: "Ubuntu", sans-serif;
  font-weight: 500;
  font-size: 14px;
  color: #ffffff;
`;

const VendorUsername = styled.span`
  font-family: "Ubuntu", sans-serif;
  font-weight: 400;
  font-size: 12px;
  color: #609f94;
  font-style: italic;
`;

const VendorAnonymous = styled.span`
  font-family: "Ubuntu", sans-serif;
  font-weight: 400;
  font-size: 12px;
  color: #838d90;
  font-style: italic;
  margin-bottom: 4px;
`;

const VendorContacts = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const ContactLink = styled.a`
  font-family: "Ubuntu", sans-serif;
  font-weight: 400;
  font-size: 12px;
  color: #24ba7d;
  text-decoration: none;
  padding: 2px 6px;
  border-radius: 4px;
  background-color: rgba(36, 186, 125, 0.1);
  border: 1px solid rgba(36, 186, 125, 0.3);

  &:hover {
    background-color: rgba(36, 186, 125, 0.2);
  }
`;

const ScrollToTopButton = styled.button<{ $show: boolean }>`
  position: fixed;
  bottom: 24px;
  right: 24px;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.6);
  border: none;
  cursor: pointer;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  backdrop-filter: blur(8px);

  opacity: ${(props) => (props.$show ? 0.8 : 0)};
  visibility: ${(props) => (props.$show ? "visible" : "hidden")};
  transform: ${(props) => (props.$show ? "translateY(0)" : "translateY(20px)")};

  &:hover {
    background: rgba(0, 0, 0, 0.8);
    opacity: ${(props) => (props.$show ? 1 : 0)};
    transform: ${(props) =>
      props.$show ? "translateY(-1px)" : "translateY(20px)"};
  }

  &:active {
    transform: ${(props) =>
      props.$show ? "translateY(0)" : "translateY(20px)"};
  }

  svg {
    width: 20px;
    height: 20px;
    fill: white;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 48px 24px;
  color: #609f94;
  font-family: "Ubuntu", sans-serif;
  font-size: 16px;
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  margin: 32px 0;
  flex-wrap: wrap;
`;

const PaginationInfo = styled.div`
  color: #8CA9A9;
  font-size: 14px;
  font-family: "Ubuntu", sans-serif;
`;

const PaginationButton = styled.button<{ $active?: boolean }>`
  padding: 8px 16px;
  border: 1px solid ${props => props.$active ? '#24B97D' : '#609F94'};
  background-color: ${props => props.$active ? '#24B97D' : 'transparent'};
  color: ${props => props.$active ? '#FFFFFF' : '#609F94'};
  border-radius: 6px;
  cursor: pointer;
  font-family: "Ubuntu", sans-serif;
  font-size: 14px;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background-color: ${props => props.$active ? '#24B97D' : 'rgba(96, 159, 148, 0.1)'};
    color: ${props => props.$active ? '#FFFFFF' : '#FFFFFF'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PaginationNumbers = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
`;

const Search: React.FC = () => {
  usePageTitle("Поиск товаров");

  const {
    searchQuery,
    conditions,
    selectedCondition,
    sortOrder,
    selectedSizes,
    setSearchQuery,
    setSelectedCondition,
    setSortOrder,
    setSelectedSizes,
    products: storeProducts,
    totalCount,
    isLoading: storeLoading,
    hasPreloaded,
  } = useSearchStore();

  const [localSearchQuery, setLocalSearchQuery] = useState(searchQuery);
  const [debouncedSearchQuery] = useDebounce(localSearchQuery, 500);
  const [currentPage, setCurrentPage] = useState(1);
  const [products, setProducts] = useState<ISearchProduct[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [visibleContacts, setVisibleContacts] = useState<Set<number>>(new Set());
  const [contactsData, setContactsData] = useState<Map<number, IStockContactResponse>>(new Map());
  const [searchCounter, setSearchCounter] = useState<number | null>(null);
  const [places, setPlaces] = useState<{ value: string; label: string }[]>([]);
  const [selectedPlace, setSelectedPlace] = useState<string>("");
  const { showError } = useToast();

  useEffect(() => {
    setSearchQuery(debouncedSearchQuery);
    setCurrentPage(1); // Сбрасываем на первую страницу при изменении поиска
  }, [debouncedSearchQuery, setSearchQuery]);

  const [showScrollToTop, setShowScrollToTop] = useState(false);

  // Используем данные из store, если они предзагружены и нет поискового запроса или фильтров
  const shouldUseStoreData = hasPreloaded && 
    !debouncedSearchQuery.trim() && 
    !selectedCondition && 
    !sortOrder && 
    selectedSizes.length === 0 &&
    (!selectedPlace || selectedPlace === "");

  // Загрузка данных
  const loadData = async (page: number = 1) => {
    if (shouldUseStoreData && page === 1) {
      setProducts(storeProducts);
      setTotalItems(totalCount);
      return;
    }

    setIsLoading(true);
    setError(null);
    
    try {
      const offset = (page - 1) * ITEMS_PER_PAGE;
      let response: ISearchResponse;
      
      if (debouncedSearchQuery.trim()) {
        response = await api.searchProductsInStock(
          debouncedSearchQuery,
          ITEMS_PER_PAGE,
          offset,
          undefined,
          selectedCondition || undefined,
          sortOrder || undefined,
          selectedSizes.length > 0 ? selectedSizes : undefined,
          selectedPlace && selectedPlace.trim() !== "" ? selectedPlace : undefined
        );
      } else {
        response = await api.getAllProductsInStock(
          ITEMS_PER_PAGE,
          offset,
          undefined,
          selectedCondition || undefined,
          sortOrder || undefined,
          selectedSizes.length > 0 ? selectedSizes : undefined,
          selectedPlace && selectedPlace.trim() !== "" ? selectedPlace : undefined
        );
      }
      
      setProducts(response.stock);
      setTotalItems(response.total_count);
    } catch (err) {
      setError('Ошибка при загрузке данных');
      console.error('Error loading data:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Загружаем данные при изменении параметров
  useEffect(() => {
    loadData(currentPage);
  }, [currentPage, selectedCondition, sortOrder, selectedSizes, debouncedSearchQuery, selectedPlace]);

  // Загружаем данные из store при первом рендере, если они есть
  useEffect(() => {
    if (shouldUseStoreData && storeProducts.length > 0) {
      setProducts(storeProducts);
      setTotalItems(totalCount);
    }
  }, [shouldUseStoreData, storeProducts, totalCount]);

  // Загрузка списка мест
  useEffect(() => {
    const loadPlaces = async () => {
      try {
        const response = await api.getStockPlaces();
        
        if (response.places && Array.isArray(response.places)) {
          const placesOptions = [
            { value: "", label: "Все места" },
            ...response.places.map((place) => ({
              value: place,
              label: place,
            }))
          ];
          setPlaces(placesOptions);
        } else {
          setPlaces([{ value: "", label: "Все места" }]);
        }
      } catch (err) {
        console.error("Error loading places:", err);
        setPlaces([]);
      }
    };
    loadPlaces();
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollToTop(window.scrollY > 500);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Пагинация
  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);
  const startItem = (currentPage - 1) * ITEMS_PER_PAGE + 1;
  const endItem = Math.min(currentPage * ITEMS_PER_PAGE, totalItems);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    scrollToTop();
  };

  const renderPaginationNumbers = () => {
    const pages = [];
    const maxVisiblePages = 7;
    
    if (totalPages <= maxVisiblePages) {
      // Показываем все страницы, если их мало
      for (let i = 1; i <= totalPages; i++) {
        pages.push(
          <PaginationButton
            key={i}
            $active={i === currentPage}
            onClick={() => handlePageChange(i)}
          >
            {i}
          </PaginationButton>
        );
      }
    } else {
      // Логика для большого количества страниц
      pages.push(
        <PaginationButton
          key={1}
          $active={1 === currentPage}
          onClick={() => handlePageChange(1)}
        >
          1
        </PaginationButton>
      );

      if (currentPage > 4) {
        pages.push(<span key="dots1" style={{ color: '#609F94' }}>...</span>);
      }

      const start = Math.max(2, currentPage - 2);
      const end = Math.min(totalPages - 1, currentPage + 2);

      for (let i = start; i <= end; i++) {
        pages.push(
          <PaginationButton
            key={i}
            $active={i === currentPage}
            onClick={() => handlePageChange(i)}
          >
            {i}
          </PaginationButton>
        );
      }

      if (currentPage < totalPages - 3) {
        pages.push(<span key="dots2" style={{ color: '#609F94' }}>...</span>);
      }

      if (totalPages > 1) {
        pages.push(
          <PaginationButton
            key={totalPages}
            $active={totalPages === currentPage}
            onClick={() => handlePageChange(totalPages)}
          >
            {totalPages}
          </PaginationButton>
        );
      }
    }

    return pages;
  };

  const formatPrice = (price: number): string => {
    return `${price.toLocaleString("ru-RU")} ₽`;
  };

  const formatNumber = (num: number): string => {
    if (num >= 1000000) {
      const millions = Math.floor(num / 100000) / 10;
      return (millions % 1 === 0 ? millions.toFixed(0) : millions.toFixed(1)) + 'M';
    }
    if (num >= 1000) {
      const thousands = Math.floor(num / 100) / 10;
      return (thousands % 1 === 0 ? thousands.toFixed(0) : thousands.toFixed(1)) + 'K';
    }
    return num.toString();
  };

  const hasValidContacts = (vendor: any) => {
    const hasTelegram =
      vendor.telegram &&
      vendor.telegram.trim() !== "" &&
      vendor.telegram !== "Пользователь не указал контакт";
    const hasVk = vendor.vk && vendor.vk.trim() !== "";
    return hasTelegram || hasVk;
  };

  const showContacts = async (productId: number) => {
    try {
      const contactData = await api.getStockContact(productId);
      setContactsData((prev) => {
        const newMap = new Map(prev);
        newMap.set(productId, contactData);
        return newMap;
      });
      setVisibleContacts((prev) => {
        if (prev.has(productId)) return prev;
        const newSet = new Set(prev);
        newSet.add(productId);
        return newSet;
      });
      // Обновляем счетчик поисков
  setSearchCounter(contactData.search_counter);
    } catch (error: any) {
      console.error('Ошибка при получении контактов:', error);

      // Показываем Toast с ошибкой
      if (error.response?.status === 403) {
        showError(
          'Превышен лимит поисков',
          'Пожалуйста, подождите обновления лимитов!'
        );
      } else if (error.message) {
        showError('Ошибка получения контактов', error.message);
      } else {
        showError('Ошибка', 'Не удалось получить контакты продавца');
      }
    }
  };

  const getVendorDisplayName = (
    vendor: any
  ): {
    firstName?: string;
    lastName?: string;
    username?: string;
    isAnonymous: boolean;
  } => {
    // Получаем данные из vendor
    const firstName = vendor.first_name || "";
    const lastName = vendor.last_name || "";
    const username = vendor.username || "";

    // Если есть хотя бы какие-то данные
    if (username.trim() || firstName.trim() || lastName.trim()) {
      return {
        firstName: firstName.trim() || undefined,
        lastName: lastName.trim() || undefined,
        username: username.trim() || undefined,
        isAnonymous: false,
      };
    }

    // Если ничего нет, возвращаем анонимное сообщение
    return { isAnonymous: true };
  };

  const conditionOptions = [
    { value: "", label: "Любое состояние" },
    ...conditions.map((condition) => ({
      value: condition.name,
      label: condition.name,
    })),
  ];

  const sortOptions = [
    { value: "", label: "Сортировка по умолчанию" },
    { value: "asc", label: "Цена: по возрастанию" },
    { value: "desc", label: "Цена: по убыванию" },
  ];

  const US_SIZES = Array.from({ length: 30 }, (_, i) => {
    const sizeValue = 3.5 + i * 0.5;
    const sizeLabel =
      sizeValue % 1 === 0
        ? `${sizeValue.toFixed(0)} US`
        : `${sizeValue.toFixed(1)} US`;
    return { value: sizeValue.toFixed(1), label: sizeLabel };
  });

  const handleConditionChange = (value: string) => {
    setSelectedCondition(value === "" ? null : value);
    setCurrentPage(1);
  };

  const handleSortOrderChange = (value: string) => {
    setSortOrder(value === "asc" || value === "desc" ? value : null);
    setCurrentPage(1);
  };

  const handleSizeChange = (sizes: string[]) => {
    setSelectedSizes(sizes);
    setCurrentPage(1);
  };

  const handlePlaceChange = (value: string) => {
    setSelectedPlace(value);
    setCurrentPage(1);
  };

  // Первичная загрузка оставшихся открытий (до первого клика показа контактов)
  useEffect(() => {
    let isMounted = true;
    if (searchCounter === null) {
      api.getUserInfo()
        .then(info => {
          if (isMounted) {
            setSearchCounter(typeof info.search_counter === 'number' ? info.search_counter : 0);
          }
        })
        .catch(() => {
          if (isMounted) setSearchCounter(0);
        });
    }
    return () => { isMounted = false; };
  }, [searchCounter]);

  // Показываем скелетоны при первой загрузке или когда загружаются данные
  const isInitialLoading = isLoading && products.length === 0;
  const isEmpty = products.length === 0 && !isLoading;

  if (isInitialLoading) {
    return (
      <MainLayout activePage="search">
        <PageContainer>
          <ProductsContainer>
            {[...Array(ITEMS_PER_PAGE)].map((_, index) => (
              <SkeletonLoader key={index} />
            ))}
          </ProductsContainer>
        </PageContainer>
      </MainLayout>
    );
  }

  return (
    <MainLayout activePage="search">
      <PageContainer>
        <HeaderContainer data-tutorial="search-header">
          <PageTitle>Поиск товаров</PageTitle>
          <SearchCounter>
            <CounterNumber>
              {searchCounter === null ? '...' : formatNumber(searchCounter)}
            </CounterNumber>
            <CounterText>осталось открытий контактов</CounterText>
          </SearchCounter>
        </HeaderContainer>

        <FiltersContainer>
          <SearchContainer data-tutorial="search-input">
            <SearchInput
              placeholder="Найти товар по названию, модели, артикулу..."
              value={localSearchQuery}
              onChange={(e) => setLocalSearchQuery(e.target.value)}
            />
            {isLoading && (
              <div
                style={{
                  marginLeft: "10px",
                  fontSize: "14px",
                  color: "#609F94",
                }}
              >
                Поиск...
              </div>
            )}
          </SearchContainer>
        </FiltersContainer>

        <FiltersContainer data-tutorial="search-filters">
          <FilterContainer>
            <CustomDropdown
              options={conditionOptions}
              value={selectedCondition || ""}
              placeholder="Выберите состояние"
              onChange={handleConditionChange}
              label="Состояние"
            />
          </FilterContainer>
          <FilterContainer>
            <CustomDropdown
              options={sortOptions}
              value={sortOrder || ""}
              placeholder="Сортировать по"
              onChange={handleSortOrderChange}
              label="Сортировка"
            />
          </FilterContainer>
          <FilterContainer>
            <MultiSelectDropdown
              options={US_SIZES}
              selectedValues={selectedSizes}
              placeholder="Выберите размер"
              onChange={handleSizeChange}
              label="Размер"
            />
          </FilterContainer>
          <FilterContainer>
            <CustomDropdown
              options={places}
              value={selectedPlace}
              placeholder="Выберите место"
              onChange={handlePlaceChange}
              label="Место"
            />
          </FilterContainer>
        </FiltersContainer>

        {isEmpty && !isLoading ? (
          <EmptyState>
            {debouncedSearchQuery
              ? "По вашему запросу товары не найдены"
              : "Товары не найдены"}
          </EmptyState>
        ) : (
          <>
            <ProductsContainer data-tutorial="search-results">
              {products.map((product) => (
                <ProductCard key={product.id}>
                  <ProductImage
                    style={{ backgroundImage: `url(${product.photo_url})` }}
                  />
                  <ConditionBadge>{product.condition.condition}</ConditionBadge>
                  <ProductName>
                    {product.brand.brand} {product.model}
                  </ProductName>
                  <ProductDetails>
                    <ProductColor>{product.color}</ProductColor>
                  </ProductDetails>
                  <ArticleInfo>
                    <ArticleLabel>Артикул</ArticleLabel>
                    <ArticleValue>{product.article}</ArticleValue>
                  </ArticleInfo>
                  <ProductInfo>
                    <SizeInfo>
                      <SizeLabel>Размер</SizeLabel>
                      <SizesContainer>
                        {product.us_size && (
                          <SizeBadge>US {product.us_size}</SizeBadge>
                        )}
                        {product.usw_size && (
                          <SizeBadge>USW {product.usw_size}</SizeBadge>
                        )}
                        {product.eu_size && (
                          <SizeBadge>EU {product.eu_size}</SizeBadge>
                        )}
                        {product.uk_size && (
                          <SizeBadge>UK {product.uk_size}</SizeBadge>
                        )}
                      </SizesContainer>
                    </SizeInfo>
                    <PriceInfo>
                      <Price>{formatPrice(product.price)}</Price>
                    </PriceInfo>
                  </ProductInfo>
                  <LocationInfo>
                    <LocationText>
                      {product.city}
                      {product.place && `, ${product.place}`}
                    </LocationText>
                    {product.fitting && <FittingBadge>Примерка</FittingBadge>}
                  </LocationInfo>
                  <VendorInfo $showContacts={visibleContacts.has(product.id)} data-tutorial="vendor-info">
                    <ShowContactsButton
                      $showContacts={visibleContacts.has(product.id)}
                      onClick={() => showContacts(product.id)}
                    >
                      Показать контакты
                    </ShowContactsButton>
                    <VendorContent $showContacts={visibleContacts.has(product.id)}>
                      <VendorLabel>Продавец</VendorLabel>
                      {(() => {
                        const contactData = contactsData.get(product.id);
                        
                        if (contactData) {
                          // Используем данные из API
                          const hasName = contactData.first_name || contactData.last_name;
                          
                          return (
                            <>
                              <VendorNameContainer>
                                {contactData.first_name && (
                                  <VendorFirstName>
                                    {contactData.first_name}
                                  </VendorFirstName>
                                )}
                                {contactData.last_name && (
                                  <VendorLastName>
                                    {contactData.last_name}
                                  </VendorLastName>
                                )}
                              </VendorNameContainer>
                              {contactData.user_name && (
                                <VendorUsername>
                                  {contactData.user_name}
                                </VendorUsername>
                              )}
                            </>
                          );
                        } else {
                          // Используем исходные данные товара
                          const vendorInfo = getVendorDisplayName(product.vendor);
                          if (vendorInfo.isAnonymous) {
                            return (
                              <VendorAnonymous>
                                Продавец предпочёл остаться анонимным
                              </VendorAnonymous>
                            );
                          }

                          return (
                            <>
                              <VendorNameContainer>
                                {vendorInfo.firstName && (
                                  <VendorFirstName>
                                    {vendorInfo.firstName}
                                  </VendorFirstName>
                                )}
                                {vendorInfo.lastName && (
                                  <VendorLastName>
                                    {vendorInfo.lastName}
                                  </VendorLastName>
                                )}
                              </VendorNameContainer>
                              {vendorInfo.username && (
                                <VendorUsername>
                                  @{vendorInfo.username}
                                </VendorUsername>
                              )}
                            </>
                          );
                        }
                      })()}
                      <VendorContacts>
                        {(() => {
                          const contactData = contactsData.get(product.id);
                          
                          if (contactData) {
                            // Используем данные из API
                            const hasTelegram = contactData.telegram && contactData.telegram.trim() !== "";
                            const hasVk = contactData.vk && contactData.vk.trim() !== "";
                            
                            if (hasTelegram || hasVk) {
                              return (
                                <>
                                  {hasTelegram && (
                                    <ContactLink
                                      href={`https://t.me/${contactData.telegram.replace("@", "")}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      TG: {contactData.telegram}
                                    </ContactLink>
                                  )}
                                  {hasVk && (
                                    <ContactLink
                                      href={contactData.vk}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      VK: {contactData.vk}
                                    </ContactLink>
                                  )}
                                </>
                              );
                            } else {
                              return (
                                <span
                                  style={{
                                    color: "#999",
                                    fontStyle: "italic",
                                    fontSize: "12px",
                                  }}
                                >
                                  Контакты не указаны
                                </span>
                              );
                            }
                          } else {
                            // Используем исходные данные товара
                            if (hasValidContacts(product.vendor)) {
                              return (
                                <>
                                  {product.vendor.telegram &&
                                    product.vendor.telegram !==
                                      "Пользователь не указал контакт" &&
                                    product.vendor.telegram.trim() !== "" && (
                                      <ContactLink
                                        href={`https://t.me/${product.vendor.telegram.replace(
                                          "@",
                                          ""
                                        )}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                      >
                                        TG: {product.vendor.telegram}
                                      </ContactLink>
                                    )}
                                  {product.vendor.vk &&
                                    product.vendor.vk.trim() !== "" && (
                                      <ContactLink
                                        href={product.vendor.vk}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                      >
                                        VK: {product.vendor.vk}
                                      </ContactLink>
                                    )}
                                </>
                              );
                            } else {
                              return (
                                <span
                                  style={{
                                    color: "#999",
                                    fontStyle: "italic",
                                    fontSize: "12px",
                                  }}
                                >
                                  Контакты не указаны
                                </span>
                              );
                            }
                          }
                        })()}
                      </VendorContacts>
                    </VendorContent>
                  </VendorInfo>
                </ProductCard>
              ))}
            </ProductsContainer>

            {/* Пагинация */}
            {totalPages > 1 && (
              <PaginationContainer>
                <PaginationButton
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  ← Назад
                </PaginationButton>
                
                <PaginationNumbers>
                  {renderPaginationNumbers()}
                </PaginationNumbers>
                
                <PaginationButton
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Вперед →
                </PaginationButton>
              </PaginationContainer>
            )}

            {/* Информация о результатах */}
            {totalItems > 0 && (
              <PaginationInfo style={{ textAlign: 'center', marginTop: '16px' }}>
                Показаны товары {startItem}-{endItem} из {totalItems.toLocaleString()}
              </PaginationInfo>
            )}
          </>
        )}
      </PageContainer>

      <ScrollToTopButton $show={showScrollToTop} onClick={scrollToTop}>
        <svg viewBox="0 0 24 24">
          <path d="M7.41 15.41L12 10.83l4.59 4.58L18 14l-6-6-6 6z" />
        </svg>
      </ScrollToTopButton>
    </MainLayout>
  );
};

export default Search;
